import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Chargement, EnTetePage, IconePlus, Message, TitreSection } from '../components/charte/Elements';
import ListeMissions from '../components/ListeMissions';
import { AIDE } from '../aide/textesAide';
import { COULEURS, texteSurClair } from '../theme/charte';
import {
  chargerDocuments, DocumentClient, enregistrerFichier, ETATS_A_REGLER, formatMontant,
} from '../donnees/documents';
import { chargerMissions, dateLivraison, devisASigner, estLivree, idMission, Mission } from '../donnees/missions';
import { jourCourt, joursDepuis, libelleFacture, situationMission } from '../donnees/situation';
import { resultatsARelire } from '../donnees/relecture';
import { telechargerExport } from '../donnees/collectes';

/* Accueil, d'après la maquette Main.dc.html : date du jour, salutation,
   « Nouvelle mission » en orange ; À faire en cartes (devis à signer en
   indigo, facture à régler, résultats livrés) ; missions en cours en
   liste ; dernières missions livrées en cartes avec l'export Excel. */

interface Tache {
  cle: string;
  genre: 'devis' | 'facture' | 'resultats';
  surtitre: string;
  titre: string;
  detail: string;
  action: string;
  lien: string;
}

const COULEUR_TACHE = { devis: '#FF7A3D', facture: COULEURS.attention, resultats: COULEURS.succes } as const;

const dateDuJour = () => new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

const pluriel = (n: number, un: string, plusieurs: string) => (n > 1 ? plusieurs : un);

const Accueil: React.FC = () => {
  const { user } = useAuth();
  const [missions, setMissions] = useState<Mission[]>([]);
  const [documents, setDocuments] = useState<DocumentClient[]>([]);
  const [chargement, setChargement] = useState(true);
  const [erreurMissions, setErreurMissions] = useState(false);
  const [erreurDocuments, setErreurDocuments] = useState(false);
  const [exportEnCours, setExportEnCours] = useState<string | null>(null);
  const [erreurExport, setErreurExport] = useState('');

  useEffect(() => { document.title = 'Accueil — Tribe'; }, []);

  useEffect(() => {
    if (!user?.email) return;
    let actif = true;
    Promise.allSettled([chargerMissions(user.email), chargerDocuments()]).then(([m, d]) => {
      if (!actif) return;
      if (m.status === 'fulfilled') setMissions(m.value); else setErreurMissions(true);
      if (d.status === 'fulfilled') setDocuments(d.value); else setErreurDocuments(true);
      setChargement(false);
    });
    return () => { actif = false; };
  }, [user?.email]);

  const parId = new Map(missions.map(m => [idMission(m), m]));
  const aSigner = devisASigner(documents, missions);
  const aRegler = documents
    .filter(d => d.type === 'FACTURE' && ETATS_A_REGLER.includes(d.etatPaiement || ''))
    .sort((a, b) => (a.dateEcheance || '').localeCompare(b.dateEcheance || ''));
  const livrees = missions.filter(estLivree).sort((a, b) => dateLivraison(b).localeCompare(dateLivraison(a)));
  const aRelire = livrees.filter(m => resultatsARelire(idMission(m), dateLivraison(m)));
  const situations = missions.map(m => ({ mission: m, situation: situationMission(m, documents) }));
  const enCours = situations.filter(s => s.situation.categorie !== 'livree' && s.situation.categorie !== 'autre');
  const enCollecte = situations.filter(s => s.situation.categorie === 'collecte').length;

  const taches: Tache[] = [
    ...aSigner.map(d => ({
      cle: `devis-${d.id}`,
      genre: 'devis' as const,
      surtitre: 'Devis à signer',
      titre: parId.get(d.questId as string)?.titre ?? d.titreMission ?? 'Mission',
      detail: `${d.numero} · ${formatMontant(d.montantTtc)} TTC`,
      action: 'Relire et signer →',
      lien: `/documents#devis-${d.id}`,
    })),
    ...aRegler.map(f => ({
      cle: `facture-${f.id}`,
      genre: 'facture' as const,
      surtitre: 'Facture à régler',
      titre: `${libelleFacture(f)} — ${parId.get(f.questId as string)?.titre ?? f.titreMission ?? 'Mission'}`,
      detail: [f.numero, f.dateEcheance ? `échéance ${jourCourt(f.dateEcheance)}` : null,
        f.resteDuTtc != null ? `reste dû ${formatMontant(f.resteDuTtc)}` : null].filter(Boolean).join(' · '),
      action: 'Voir la facture →',
      lien: '/documents',
    })),
    ...aRelire.map(m => ({
      cle: `resultats-${idMission(m)}`,
      genre: 'resultats' as const,
      surtitre: 'Résultats livrés',
      titre: m.titre || 'Mission',
      detail: `${(m.collectesRealisees ?? 0).toLocaleString('fr-FR')} collectes · livré le ${jourCourt(dateLivraison(m))}`,
      action: 'Consulter les résultats →',
      lien: `/missions/${idMission(m)}?onglet=resultats`,
    })),
  ];

  const decisions = aSigner.length + aRegler.length;
  const resume = [
    enCollecte === 0 ? 'Aucune mission en collecte' : `${enCollecte === 1 ? 'Une' : enCollecte} ${pluriel(enCollecte, 'mission', 'missions')} en collecte`,
    decisions === 0 ? 'rien ne vous attend.' : `${decisions === 1 ? 'une' : decisions} ${pluriel(decisions, 'décision vous attend', 'décisions vous attendent')}.`,
  ].join(', ');
  const prenom = (user?.nom || '').trim().split(/\s+/)[0];

  const exporter = async (m: Mission) => {
    const id = idMission(m);
    setExportEnCours(id);
    setErreurExport('');
    try {
      const { blob, nom } = await telechargerExport(id);
      enregistrerFichier(blob, nom);
    } catch (err: any) {
      setErreurExport(!err?.response
        ? 'Impossible de joindre Tribe. Vérifiez votre connexion puis réessayez.'
        : "L'export n'a pas pu être préparé. Réessayez dans quelques minutes.");
    } finally {
      setExportEnCours(null);
    }
  };

  return (
    <div className="page" style={{ gap: '32px' }}>
      <EnTetePage
        surtitre={dateDuJour()}
        titre={prenom ? `Bonjour, ${prenom}.` : 'Bonjour.'}
        sousTitre={chargement ? undefined : resume}
        actions={<Link to="/missions/nouvelle" className="bouton bouton--grand"><IconePlus />Nouvelle mission</Link>}
      />

      {chargement ? <Chargement /> : (
        <>
          {(erreurMissions || erreurDocuments) && (
            <Message genre="erreur">
              {erreurMissions && erreurDocuments
                ? 'Impossible de charger vos missions et vos documents. Vérifiez votre connexion puis rechargez la page.'
                : erreurMissions
                  ? 'Impossible de charger vos missions. Vérifiez votre connexion puis rechargez la page.'
                  : 'Impossible de charger vos documents. Vérifiez votre connexion puis rechargez la page.'}
            </Message>
          )}

          <section aria-labelledby="titre-a-faire" className="pile" style={{ gap: '14px' }}>
            <TitreSection titre="À faire" id="titre-a-faire" aide={AIDE.aFaire}
              compteur={`${taches.length} ${pluriel(taches.length, 'élément', 'éléments')}`} />
            {taches.length === 0 ? (
              <div className="bloc"><p className="texte-secondaire">Rien ne vous attend pour le moment.</p></div>
            ) : (
              <div className="grille-3">
                {taches.map(t => (
                  <Link key={t.cle} to={t.lien} className={`carte${t.genre === 'devis' ? ' carte--forte' : ''}`} style={{ minHeight: '150px' }}>
                    <div className="etiquette-mono" style={{ color: texteSurClair(COULEUR_TACHE[t.genre]) }}>{t.surtitre}</div>
                    <div className="carte__titre">{t.titre}</div>
                    <div className="carte__detail">{t.detail}</div>
                    <div className="carte__action" style={{ color: texteSurClair(COULEUR_TACHE[t.genre]) }}>{t.action}</div>
                  </Link>
                ))}
              </div>
            )}
          </section>

          <section aria-labelledby="titre-en-cours" className="pile" style={{ gap: '14px' }}>
            <TitreSection titre="Missions en cours" id="titre-en-cours"
              action={<Link to="/missions" style={{ fontSize: '14px', fontWeight: 600 }}>Toutes les missions →</Link>} />
            <ListeMissions variante="accueil" lignes={enCours} vide="Aucune mission en cours pour le moment." />
          </section>

          <section aria-labelledby="titre-livrees" className="pile" style={{ gap: '14px' }}>
            <TitreSection titre="Dernières missions livrées" id="titre-livrees" />
            {erreurExport && <Message genre="erreur" onFermer={() => setErreurExport('')}>{erreurExport}</Message>}
            {livrees.length === 0 ? (
              <div className="bloc"><p className="texte-secondaire">Aucune mission livrée pour le moment.</p></div>
            ) : (
              <div className="grille-3">
                {livrees.slice(0, 3).map(m => {
                  const fraicheur = joursDepuis(dateLivraison(m));
                  return (
                    <article key={idMission(m)} className="carte" style={{ gap: '10px', padding: '20px' }} data-testid={`livree-${idMission(m)}`}>
                      <div className="ligne ligne--entre">
                        <span className="etiquette-mono" style={{ color: COULEURS.succes, letterSpacing: '0.14em' }}>
                          Livrée · {jourCourt(dateLivraison(m))}
                        </span>
                        {fraicheur != null && <span style={{ fontSize: '12px', color: COULEURS.texteDiscret }}>Fraîcheur {fraicheur} j</span>}
                      </div>
                      <Link to={`/missions/${idMission(m)}?onglet=resultats`} style={{ fontWeight: 600, fontSize: '15px', color: COULEURS.texte }}>
                        {m.titre || 'Mission sans titre'}
                      </Link>
                      <div style={{ fontSize: '13px', color: COULEURS.texteSecondaire }}>
                        {(m.collectesRealisees ?? 0).toLocaleString('fr-FR')} collectes livrées
                      </div>
                      <button type="button" className="bouton bouton--discret" style={{ alignSelf: 'flex-start', fontSize: '14px', marginTop: '4px' }}
                        onClick={() => exporter(m)} disabled={exportEnCours === idMission(m)}>
                        {exportEnCours === idMission(m) ? 'Préparation…' : 'Exporter en Excel'}
                      </button>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default Accueil;
